/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as React from 'react';
import { useContext, useState } from 'react';
import { Field, Form } from 'react-final-form';
import { useParams } from 'react-router-dom';
import { FORM_ERROR } from 'final-form';
import { css, StyleSheet } from 'aphrodite';

import { ActionButton, LinkButton, LinkButtonWrapper, BrandColors, AlertBar } from '@blend/enterprise-components';

import { MeContext } from 'blb-contexts/Me';
import { DOCUMENT_DEFINITIONS } from 'blb-constants/DocumentTypes';
import { BlendBridge } from 'blb-types/blendBridge';
import { errorMessage } from 'blb-utils/errors';

import { messageInputConfig } from './inputConfig';
import type { ReduxProps } from './NewMessageContainer';
import { InboxContext } from '../Inbox/InboxContext';
import { AttachmentRepeater } from '../NewThreadModal/AttachmentRepeater';
import { Attachment } from '../../types';
import { formatUserForMessage } from '../../utils/threadUtils';
import { DocUploadModal } from '../../../DocUploadModal';

const styles = StyleSheet.create({
  actionsWrapper: {
    backgroundColor: BrandColors.WHITE,
    border: `1px solid ${BrandColors.CLOUD_50}`,
    marginBottom: 10,
    marginTop: -6,
    overflow: 'hidden',
    padding: '10px 14px',
  },
  AttachmentRepeaterWrapper: {
    marginBottom: 10,
  },
  buttonWrapper: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
  },
  errorWrapper: {
    marginBottom: 10,
  },
  padding: {
    padding: '16px 24px',
  },
});

// Passing this so we know the documnet is uploaded from Shared Inbox
const SHARED_INDBOX_DOCUMENT_TARGETS = ['SHARED_INBOX'];

interface NewMessageForm {
  message: string;
}

export type NewMessageProps = ReduxProps;
export const NewMessage: React.FC<NewMessageProps> = ({ selectedThread, createMessageForLoan, user }) => {
  const {
    stylingConfig: { additionalPadding, singleItemPerRow },
  } = useContext(InboxContext);
  const { loanId } = useParams<{ loanId: string }>();
  const [attachmentModalOpen, setAttachmentModalOpen] = useState<boolean>(false);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const me = useContext(MeContext);

  const generateMessage = (values: NewMessageForm): BlendBridge.API.PostInboxMessageBody => {
    const { message } = values;
    return {
      attachments,
      body: message,
      user: formatUserForMessage(user),
    };
  };

  const sendMessage = async (values: NewMessageForm) => {
    try {
      const message = generateMessage(values);
      await createMessageForLoan(loanId, selectedThread!.id, message);
      setAttachments([]);
    } catch (error) {
      return {
        [FORM_ERROR]: errorMessage('SENDING_MESSAGE', me?.principalType),
      };
    }

    return { submitSucceeded: true };
  };

  const handleRemoveAttachment = (index: number) => {
    const items = attachments.slice();
    items.splice(index, 1);
    setAttachments(items);
  };

  return (
    <Form onSubmit={sendMessage}>
      {({ handleSubmit, submitError, submitting, pristine, form }) => {
        return (
          <form
            className={css(additionalPadding && styles.padding)}
            id="new-message-form"
            onSubmit={async event => {
              const formState = await handleSubmit(event);
              if (formState?.submitSucceeded) {
                form.reset();
              }
            }}
          >
            {submitError && (
              <div className={css(styles.errorWrapper)}>
                <AlertBar id={`new-thread-error`} message={submitError} theme="error" />
              </div>
            )}
            <Field {...messageInputConfig} />
            <div className={css(styles.actionsWrapper)}>
              {attachments.length > 0 && (
                <div className={css(styles.AttachmentRepeaterWrapper)}>
                  <AttachmentRepeater
                    attachments={attachments}
                    handleRemove={handleRemoveAttachment}
                    singleItemPerRow={singleItemPerRow}
                  />
                </div>
              )}
              <div className={css(styles.buttonWrapper)}>
                <LinkButtonWrapper>
                  <LinkButton
                    icon="attach_file"
                    id="it-CreateNewMessage-AttachmentButton"
                    label="Attach Files"
                    onClick={() => setAttachmentModalOpen(true)}
                  />
                </LinkButtonWrapper>
                <ActionButton
                  disabled={pristine}
                  icon="send"
                  id="it-CreateNewMessage-submit"
                  isLoading={submitting}
                  label="Send"
                  type="submit"
                />
              </div>
            </div>
            <DocUploadModal
              allowMultipleUploads={true}
              documentTargets={SHARED_INDBOX_DOCUMENT_TARGETS}
              handleClose={() => setAttachmentModalOpen(false)}
              isOpen={attachmentModalOpen}
              onSuccess={(uploadedAttachments: Attachment[]) => setAttachments(attachments.concat(uploadedAttachments))}
              selectableDocTypes={DOCUMENT_DEFINITIONS}
              title="Attach Documents"
            />
          </form>
        );
      }}
    </Form>
  );
};
